import { useField } from "formik";
import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SelectFieldProps {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  /** Called after Formik state updates so the parent can autosave on change. */
  onCommit?: (name: string, value: string) => void;
}

/** Formik-bound shadcn Select. Selects commit on change (there's no blur). */
export function SelectField({ name, label, options, placeholder, onCommit }: SelectFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Select
        value={field.value || ""}
        onValueChange={(value) => {
          helpers.setValue(value);
          helpers.setTouched(true);
          onCommit?.(name, value);
        }}
      >
        <SelectTrigger id={name} className="w-full" aria-invalid={hasError}>
          <SelectValue placeholder={placeholder ?? "Select…"} />
        </SelectTrigger>
        <SelectContent position="popper">
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

interface RadioFieldProps {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  onCommit?: (name: string, value: string) => void;
}

/** Formik-bound shadcn RadioGroup (horizontal). */
export function RadioField({ name, label, options, onCommit }: RadioFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <RadioGroup
        value={field.value || ""}
        onValueChange={(value) => {
          helpers.setValue(value);
          helpers.setTouched(true);
          onCommit?.(name, value);
        }}
        className="flex flex-wrap gap-6"
      >
        {options.map((opt) => (
          <div key={opt.value} className="flex items-center gap-2">
            <RadioGroupItem value={opt.value} id={`${name}-${opt.value}`} />
            <Label htmlFor={`${name}-${opt.value}`} className="font-normal">
              {opt.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

interface CheckboxFieldProps {
  name: string;
  label: ReactNode;
  onCommit?: (name: string, value: boolean) => void;
}

/** Formik-bound shadcn Checkbox that stores a boolean. */
export function CheckboxField({ name, label, onCommit }: CheckboxFieldProps) {
  const [field, , helpers] = useField<boolean>(name);
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={name}
        checked={!!field.value}
        onCheckedChange={(checked) => {
          const val = checked === true;
          helpers.setValue(val);
          helpers.setTouched(true);
          onCommit?.(name, val);
        }}
      />
      <Label htmlFor={name} className="font-normal">
        {label}
      </Label>
    </div>
  );
}
